import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { BaseContext } from "../../../../../../contexts/BaseContext";
import { GraphContext } from "../../../../../../contexts/GraphContext";
import { Database, Zap, Info, Layers, Filter, RefreshCw } from "lucide-react";
import { cleanSchema } from "./AutoSuggestUtils";
import {
  metadataService,
  tagService,
  suggestionService,
} from "../../../../../../services/api";
import toast from "react-hot-toast";
import { Modal } from "@mui/material";
import {
  classify,
  prepareData,
} from "../../../../DeasyConfig/ConfigElements/useJobProgress";
import { nodePathToConditions } from "../GraphUtils";
import { PathNavigator } from "./AutoSuggestUtils";
import { getNewConditionsFromNodes } from "../ExtractMetadata/components/Utils";
import * as Sentry from "@sentry/react";
import { GRAPH_NODE_TYPES } from "../GraphUtils";
import { FilePickerModal } from "../../../../../utils/FilePickerModal";
import MetadataTable from "../../MetadataTable/MetadataTable";
import { TagSelectionModal } from "./TagSelectionModal";
import { v4 as uuidv4 } from "uuid";
import { PortalTooltip } from "./PortalTooltip";

const FLAT_VAR = "flat";
const MAX_NUMBER_OF_TAGS = 30;

function InfoWithTooltip({ tooltip, ...iconProps }) {
  const iconRef = useRef();
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const handleMouseEnter = () => {
    const rect = iconRef.current.getBoundingClientRect();
    setCoords({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX + rect.width / 2,
    });
    setShow(true);
  };

  return (
    <>
      <span
        ref={iconRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShow(false)}
        {...iconProps}
      >
        <Info size={18} className="text-primary cursor-help !opacity-100" />
      </span>
      {show && (
        <PortalTooltip
          style={{
            top: coords.top - 48,
            left: coords.left - 112,
            width: 224,
            position: "absolute",
          }}
          className="p-2 bg-gray-800 text-white text-xs rounded shadow-lg"
        >
          {tooltip}
        </PortalTooltip>
      )}
    </>
  );
}

export const AutoSuggestedMetadata = ({
  generatingSchema,
  setGeneratingSchema,
}) => {
  const {
    deasyUserConfig,
    vdbFilesCount,
    unsyncedVDBFilesCount,
    metadataReadinessStatus,
    setSavedTags,
    savedTags,
    setUpdatedTags,
    vdbLastActiveProfileName,
    selectedDataSlice,
    totalActiveFiles,
  } = useContext(BaseContext);

  const {
    discoveryGraphData,
    updateDiscoveryGraphData,
    selectedDiscoveryGraph,
    handleSaveGraph,
    selectedNodeData,
    startAtomicUpdate,
    hierarchyStats,
  } = useContext(GraphContext);

  const {
    llmConfig: { LastActive: llmLastActive },
    deasyApiKey,
  } = deasyUserConfig;

  const scope = "file";
  const [graphTagType, setGraphTagType] = useState(GRAPH_NODE_TYPES.OPEN_ENDED);
  const useNetNew = true;
  const [inputTextContext, setInputTextContext] = useState("");
  const [maxDepth, setMaxDepth] = useState(1);
  const maxNumberOfTags = MAX_NUMBER_OF_TAGS;
  const [disableAutoSuggest, setDisableAutoSuggest] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showMiniWarning, setShowMiniWarning] = useState(false);
  const [showPrepareDataDialog, setShowPrepareDataDialog] = useState(false);
  const [oobTaggedFileCount, setOobTaggedFileCount] = useState(0);
  const [loadingOOBTaggedFileCount, setLoadingOOBTaggedFileCount] =
    useState(false);
  const structureType = FLAT_VAR;
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [openFilePicker, setOpenFilePicker] = useState(false);
  const [showMetadataTable, setShowMetadataTable] = useState(false);
  const [showTagSelectionModal, setShowTagSelectionModal] = useState(false);
  const [suggestedTagsData, setSuggestedTagsData] = useState({});
  const [suggestedCreatedTags, setSuggestedCreatedTags] = useState({});
  const [tagNotFoundRates, setTagNotFoundRates] = useState({});
  const [isPrepareLoading, setIsPrepareLoading] = useState(false);
  const [tableConditionsNew, setTableConditionsNew] = useState(null);
  const [localFilters, setLocalFilters] = useState({});
  const [useDefaultMaxValues, setUseDefaultMaxValues] = useState(true);
  const useDeepSuggestionMode = graphTagType === GRAPH_NODE_TYPES.HIERARCHY;
  const [showTagFormatDropdown, setShowTagFormatDropdown] = useState(false);
  const [showMaxValuesDropdown, setShowMaxValuesDropdown] = useState(false);

  // Automatically set maxDepth to 2 when hierarchical tag type is selected
  useEffect(() => {
    if (graphTagType === GRAPH_NODE_TYPES.HIERARCHY) {
      setMaxDepth(2);
    }
  }, [graphTagType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".tag-format-dropdown")) {
        setShowTagFormatDropdown(false);
      }
      if (!event.target.closest(".max-values-dropdown")) {
        setShowMaxValuesDropdown(false);
      }
    };

    if (showTagFormatDropdown || showMaxValuesDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showTagFormatDropdown, showMaxValuesDropdown]);

  // const handleStructureTypeChange = (type) => {
  //   setStructureType(type);
  //   setGraphTagType(type === HIERARCHY_VAR && GRAPH_NODE_TYPES.OPEN_ENDED);
  //   setMaxDepth(
  //     type === FLAT_VAR ? DEFAULT_DEPTH_FLAT : DEFAULT_DEPTH_HIERARCHY,
  //   );
  // };

  const getFileCountForNode = (hierarchyStats, nodePath) => {
    if (!nodePath?.length) {
      return hierarchyStats?.file_count || 0;
    }

    let current = hierarchyStats;
    if (!hierarchyStats) return 0;

    for (let i = 0; i < nodePath.length; i++) {
      const pathSegment = nodePath[i];
      const matchingNode = current.children?.find(
        (child) => child.name?.toLowerCase() === pathSegment?.toLowerCase(),
      );
      if (!matchingNode) return 0;
      current = matchingNode;
    }
    return current.file_count || 0;
  };

  const getTotalDatasetsCount = useCallback(
    (hierarchyStats, nodePath) => {
      if (!nodePath?.length) {
        return totalActiveFiles || hierarchyStats?.file_count || 0;
      }
      return getFileCountForNode(hierarchyStats, nodePath);
    },
    [totalActiveFiles],
  );

  const allFilesSynthesized = useMemo(() => {
    const totalCount = getTotalDatasetsCount(
      hierarchyStats,
      selectedNodeData?.nodePath,
    );
    return totalCount > 0 && oobTaggedFileCount === totalCount;
  }, [
    hierarchyStats,
    selectedNodeData,
    getTotalDatasetsCount,
    oobTaggedFileCount,
  ]);

  const updateGraphData = (suggestion, nodePath, suggestionRoot) => {
    // Create a deep copy of the discovery graph data
    const newGraphData = JSON.parse(JSON.stringify(discoveryGraphData || {}));

    let currentNode = newGraphData;
    for (let i = 0; i < nodePath?.length - 1; i++) {
      if (!currentNode[nodePath[i]]) {
        currentNode[nodePath[i]] = {};
      }
      currentNode = currentNode[nodePath[i]];
    }

    const cleanedSuggestion = cleanSchema(suggestion);

    // Helper function to ensure value nodes have correct configuration
    const ensureValueNodes = (node, autoSuggestion) => {
      if (!node || typeof node !== "object") return node;
      Object.entries(node).forEach(([key, value]) => {
        // Only process if the key exists in the suggestion
        if (key in autoSuggestion) {
          if (value?.TagAvailableValues) {
            // For flat structure, always set to ['<ANY>']
            // For hierarchical, only set to ['<ANY>'] if no values specified
            // if we are suggesting from document type, we don't want to set the default values
            if (
              key !== "Document Type" &&
              ((structureType === FLAT_VAR &&
                (graphTagType === GRAPH_NODE_TYPES.OPEN_ENDED ||
                  graphTagType === GRAPH_NODE_TYPES.BINARY)) ||
                !Array.isArray(value.TagAvailableValues) ||
                value.TagAvailableValues.length === 0)
            ) {
              node[key] = {
                ...value,
                TagAvailableValues:
                  graphTagType === GRAPH_NODE_TYPES.BINARY
                    ? ["Yes", "No"]
                    : ["<ANY>"],
              };
            }
          } else if (typeof value === "object") {
            ensureValueNodes(value, autoSuggestion[key] || {});
          }
        }
      });
      return node;
    };

    if (!nodePath?.length) {
      Object.keys(cleanedSuggestion).forEach((key) => {
        currentNode[key] = cleanedSuggestion[key];
      });
    } else {
      // Add safety check before assigning
      if (
        cleanedSuggestion &&
        typeof cleanedSuggestion === "object" &&
        !Array.isArray(cleanedSuggestion)
      ) {
        currentNode[nodePath.at(-1)] = cleanedSuggestion;
      } else {
        console.error("Invalid cleanedSuggestion:", cleanedSuggestion);
        currentNode[nodePath.at(-1)] = {}; // Set empty object as fallback
      }
    }

    // Process the entire graph to ensure value nodes
    ensureValueNodes(newGraphData, cleanedSuggestion);
    return newGraphData;
  };

  const updateTagsFromSuggestion = async (createdTags) => {
    const updatedTags = {};
    const newTags = [];

    const existingTagsMap = savedTags.reduce((map, tag) => {
      map[tag.name] = tag;
      return map;
    }, {});
    if (Array.isArray(createdTags)) {
      for (const tag of createdTags) {
        const tagName = tag.deasy_tag.name;
        if (existingTagsMap[tagName]) {
          updatedTags[tagName] = tag.new_available_values;
        }
        newTags.push(tag.deasy_tag);
      }
    } else if (createdTags && typeof createdTags === "object") {
      Object.entries(createdTags).forEach(([tagName, tagData]) => {
        if (tagData.available_values && tagData.available_values.length > 0) {
          if (existingTagsMap[tagName]) {
            updatedTags[tagName] = tagData.available_values;
          }
          newTags.push(tagData);
        }
      });
    } else {
      console.warn("Invalid tag data format:", createdTags);
    }

    setUpdatedTags(updatedTags);

    const tags = await tagService.getSavedTags(deasyApiKey);
    if (Object.keys(updatedTags).length > 0) {
      toast.success(
        <div className="space-y-2">
          <p>The following tags have new available values:</p>
          <ul className="list-disc list-inside">
            {Object.keys(updatedTags).map((tagName) => (
              <li key={tagName} className="font-medium">
                {tagName}
              </li>
            ))}
          </ul>
          <p>
            Please review these tags via their tag modals to standardize values
            and re-run existing files.
          </p>
        </div>,
        {
          duration: 3000,
        },
      );
    }
    setSavedTags(tags.data.tags || []);
  };

  const handleAutoSuggest = async () => {
    if (generatingSchema) return;

    const endAtomicUpdate = startAtomicUpdate();

    try {
      setGeneratingSchema(true);
      const nodeLabel =
        selectedNodeData?.nodeType === "value"
          ? selectedNodeData.nodePath.at(-2)
          : selectedNodeData?.label;

      const nodeObject = selectedNodeData
        ? {
            data: {
              nodePath: selectedNodeData.nodePath || [],
            },
          }
        : null;

      const nodeConditions = getNewConditionsFromNodes(
        nodeObject ? [nodeObject] : [],
      );

      let conditions = nodeConditions;

      if (
        tableConditionsNew &&
        Object.keys(tableConditionsNew).length > 0 &&
        tableConditionsNew.children &&
        tableConditionsNew.children.length > 0
      ) {
        conditions = {
          condition: "AND",
          children: [],
        };

        if (nodeConditions && Object.keys(nodeConditions).length > 0) {
          if (nodeConditions.condition && nodeConditions.children) {
            conditions.children.push(nodeConditions);
          } else {
            conditions.children.push({
              condition: "AND",
              children: [{ tag: nodeConditions }],
            });
          }
        }

        conditions.children.push(tableConditionsNew);
      }
      if (window.startTrackerGracePeriod) {
        window.startTrackerGracePeriod(5 * 60000);
      }
      const response = await suggestionService.suggestTags(
        vdbLastActiveProfileName,
        llmLastActive,
        discoveryGraphData,
        {
          label: nodeLabel,
          path: selectedNodeData.nodePath,
        },
        inputTextContext,
        scope,
        [],
        structureType === FLAT_VAR ? 1 : maxDepth,
        deasyApiKey,
        {
          use_net_new_tags: useNetNew,
          use_mix_llm_and_source: useNetNew,
          structure_type: structureType,
          output_type: graphTagType,
          max_number_of_tags: maxNumberOfTags,
          use_deep_suggestion_mode: useDeepSuggestionMode,
        },
        conditions,
        useDefaultMaxValues,
        null,
        selectedDiscoveryGraph?.graph_name,
        null,
        selectedDataSlice?.id || null,
        useDeepSuggestionMode,
      );

      if (!response?.data?.suggestion) {
        throw new Error("Invalid response format from suggestion service");
      }

      const suggestion = response.data.suggestion;
      const suggestedTags = response.data.suggested_tags;
      const tagNotFoundRates = response.data.tag_not_found_rates || {};
      const enhancedSuggestion = JSON.parse(JSON.stringify(suggestion));

      Object.keys(enhancedSuggestion).forEach((tagName) => {
        if (suggestedTags[tagName]) {
          enhancedSuggestion[tagName].description =
            suggestedTags[tagName].description || "";
          enhancedSuggestion[tagName].output_type =
            suggestedTags[tagName].output_type || "string";
          enhancedSuggestion[tagName].maxValues =
            suggestedTags[tagName].maxValues;
        }
      });

      setSuggestedTagsData(enhancedSuggestion);
      setTagNotFoundRates(tagNotFoundRates);

      setShowTagSelectionModal(true);
      setSuggestedCreatedTags(suggestedTags || {});
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Unknown error occurred";
      toast.error(errorMessage);
      Sentry.captureException(error);
      console.error("Auto-suggest failed:", error);
      endAtomicUpdate();
      setGeneratingSchema(false);
    }
  };
  const handleTagsConfirmed = async (confirmedTags, selectedTagNames) => {
    const endAtomicUpdate = startAtomicUpdate();

    // Create a toast ID to update the same toast
    let toastId;

    const showProgressToast = (progress, step) => {
      const content = (
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="font-medium text-sm">{step}</p>
            <div className="w-full h-2 bg-gray-200 rounded-full mt-1 overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-200 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-right mt-0.5 text-gray-500">
              {Math.round(progress)}%
            </p>
          </div>
        </div>
      );

      if (toastId) {
        toast.loading(content, { id: toastId });
      } else {
        toastId = toast.loading(content, { duration: Infinity });
      }

      return toastId;
    };
    try {
      // Start progress toast
      showProgressToast(10, "Consolidating tags...");

      const confirmedTagsData = {};

      Object.keys(confirmedTags).forEach((tagName) => {
        if (suggestedCreatedTags[tagName]) {
          confirmedTagsData[tagName] = suggestedCreatedTags[tagName];
          console.log(`Found exact match for tag: ${tagName}`);
        } else {
          console.log(`No match found for tag: ${tagName}`);
        }
      });

      selectedTagNames.forEach((tagName) => {
        if (suggestedCreatedTags[tagName]) {
          confirmedTagsData[tagName] = suggestedCreatedTags[tagName];
          console.log(`Found match for nested tag: ${tagName}`);
        } else {
          console.log(`No match found for nested tag: ${tagName}`);
        }
      });

      showProgressToast(20, "Creating new tags...");
      const tagData = Object.values(confirmedTagsData);
      try {
        const tagUpsertResponse = await tagService.bulkUpsertTags(
          tagData,
          deasyApiKey,
        );
        if (tagUpsertResponse.data?.failed_tags?.length > 0) {
          toast.error("Failed to save suggested tags. Please try again.");
          Sentry.captureException(tagUpsertResponse.data.failed_tags);
        }
      } catch (error) {
        console.error("Failed to upsert tags:", error);
        toast.error("Failed to user some tags");
        Sentry.captureException(error);
      }
      showProgressToast(40, "Saving schema to database...");

      const updatedGraphData = updateGraphData(
        confirmedTags,
        selectedNodeData.nodePath,
      );
      updateDiscoveryGraphData(updatedGraphData);

      showProgressToast(60, "Synchronizing tag information...");
      await Promise.all([
        handleSaveGraph({
          ...selectedDiscoveryGraph,
          graph_data: updatedGraphData,
        }),
        updateTagsFromSuggestion(confirmedTagsData),
      ]);

      // Complete progress and dismiss toast after a delay
      showProgressToast(100, "Completed successfully!");

      // Replace loading toast with success toast
      setTimeout(() => {
        toast.success("Schema updated successfully", {
          id: toastId,
          duration: 500,
        });
      }, 100);
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Unknown error occurred";

      // Show error in toast
      if (toastId) {
        toast.error(errorMessage, { id: toastId, duration: 100 });
      } else {
        toast.error(errorMessage);
      }

      Sentry.captureException(error);
      console.error("Failed to update graph with confirmed tags:", error);
    } finally {
      endAtomicUpdate();
      setGeneratingSchema(false);
      setShowTagSelectionModal(false);
    }
  };

  const fetchInitialTagStatistics = async () => {
    if (vdbLastActiveProfileName && deasyApiKey) {
      try {
        setLoadingOOBTaggedFileCount(true);
        const conditions = nodePathToConditions(
          selectedNodeData?.nodePath || [],
        );
        const response = await metadataService.getOOBTaggedFileCount(
          vdbLastActiveProfileName,
          conditions,
          deasyApiKey,
          selectedDataSlice?.id || null,
        );
        setOobTaggedFileCount(response.data.fully_tagged_files || 0);
      } catch (error) {
        console.error("Failed to fetch initial OOB tagged files count:", error);
        Sentry.captureException(error);
      } finally {
        setLoadingOOBTaggedFileCount(false);
      }
    }
  };
  useEffect(
    () => {
      fetchInitialTagStatistics();
    },
    // eslint-disable-next-line
    [
      vdbLastActiveProfileName,
      deasyApiKey,
      selectedNodeData?.nodePath,
      selectedDataSlice?.id,
    ],
  );

  useEffect(() => {
    const readyFilesCount = vdbFilesCount - unsyncedVDBFilesCount;
    setDisableAutoSuggest(readyFilesCount === 0);
  }, [vdbFilesCount, unsyncedVDBFilesCount, metadataReadinessStatus]);

  const handleAutoSuggestClick = () => {
    if (generatingSchema || disableAutoSuggest) return;

    // Check if using GPT-4o-mini
    const currentLLMConfig =
      deasyUserConfig?.llmConfig?.Configs[
        deasyUserConfig?.llmConfig?.LastActive
      ];
    if (
      currentLLMConfig?.model ||
      currentLLMConfig?.llmModel === "gpt-4o-mini"
    ) {
      setShowMiniWarning(true);
      return;
    }

    setShowConfirmDialog(true);
  };

  const handlePrepareAll = () => {
    setIsPrepareLoading(true);
    // Don't close the modal immediately - keep it open during loading
    // setShowPrepareDataDialog(false);

    const nodeFileCount = {
      total_files: getTotalDatasetsCount(
        hierarchyStats,
        selectedNodeData?.nodePath,
      ),
    };

    try {
      prepareData(
        vdbLastActiveProfileName, // vdb profile name
        llmLastActive, // llm profile name
        deasyApiKey, // deasy api key
        nodeFileCount, // node file count
      );

      const jobID = uuidv4();
      const pendingJob = {
        tracker_id: jobID,
        task_type: "classify",
        status: "pending",
        start_time: new Date().toISOString(),
        progress: 0,
        total_files: nodeFileCount?.total_files,
        processed_files: 0,
        file_details: {
          total: nodeFileCount?.total_files,
          processed: 0,
          successful: 0,
          failed: 0,
        },
        additional_stats: {
          tags_created: 0,
          tags_selected: 10,
        },
        is_pending: true,
      };

      const existingPendingJobs = JSON.parse(
        localStorage.getItem("pendingProgressJobs") || "[]",
      );
      const updatedPendingJobs = [...existingPendingJobs, pendingJob];
      localStorage.setItem(
        "pendingProgressJobs",
        JSON.stringify(updatedPendingJobs),
      );

      // Trigger the MiniProgressTracker grace period to look for the new job
      if (window.startTrackerGracePeriod) {
        window.startTrackerGracePeriod(5 * 60000); // 5 minute grace period
      }

      // Delay toast notification and state reset for 12 seconds
      setTimeout(() => {
        // Show toast notification
        toast.success(
          "Data preparation started! Track progress in the bottom-left or navigate to the Progress Tracker page.",
          { duration: 6000 },
        );

        // Reset loading state and close the modal after delay
        setIsPrepareLoading(false);
        setShowPrepareDataDialog(false);
      }, 12000);
    } catch (error) {
      console.error("Failed to start data preparation:", error);
      Sentry.captureException(error);
      toast.error("Failed to start data preparation");
      setIsPrepareLoading(false);
      setShowPrepareDataDialog(false);
    }
  };

  const handlePrepareSelected = () => {
    setShowPrepareDataDialog(false);
    setOpenFilePicker(true);
  };

  const handleFilesSelected = useCallback(
    () => {
      if (selectedFiles && selectedFiles.length > 0) {
        setIsPrepareLoading(true);

        try {
          classify(
            vdbLastActiveProfileName, // vdb profile name
            llmLastActive, // llm profile name
            selectedFiles, // selected files
            deasyApiKey, // deasy api key
            null, // tags
            {}, // current tree
            false, // soft run
            false, // overwrite
            false, // blocking call
            selectedDiscoveryGraph?.graph_name, // hierarchy name
          );

          // Trigger the MiniProgressTracker grace period to look for the new job
          if (window.startTrackerGracePeriod) {
            window.startTrackerGracePeriod(5 * 60000); // 5 minute grace period
          }

          // Delay toast notification and state reset for 12 seconds
          setTimeout(() => {
            // Show toast notification
            toast.success(
              "Data preparation started! Track progress in the bottom-left or navigate to the Progress Tracker page.",
              { duration: 6000 },
            );

            // Reset loading state and selected files
            setIsPrepareLoading(false);
            setSelectedFiles([]);
          }, 12000);
        } catch (error) {
          console.error("Failed to start data preparation:", error);
          Sentry.captureException(error);
          toast.error("Failed to start data preparation");
          setIsPrepareLoading(false);
          setSelectedFiles([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFiles, vdbLastActiveProfileName, llmLastActive, deasyApiKey],
  );

  useEffect(() => {
    if (!openFilePicker && selectedFiles.length > 0 && !isPrepareLoading) {
      handleFilesSelected();
    }
  }, [openFilePicker, selectedFiles, handleFilesSelected, isPrepareLoading]);

  const fetchDataStats = async () => {
    try {
      await fetchInitialTagStatistics();
    } catch (error) {
      console.error("Failed to refresh data stats:", error);
      Sentry.captureException(error);
      toast.error("Failed to refresh data stats");
    }
  };

  return (
    <div className="flex flex-col p-4 space-y-4 w-full text-left">
      {/* Current Node Information as Header */}
      <div className="flex flex-col">
        <h2 className="text-xl font-semibold text-primary">
          <PathNavigator nodePath={selectedNodeData?.nodePath || []} />
        </h2>
        <div className="text-sm text-gray-600 mt-1">
          Let Deasy suggest the best representation of your data in an organized
          structure
        </div>
      </div>

      {/* Stats Section - Compact, standardized width and spacing */}
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-gray-700">Data Overview</h3>
          <button
            onClick={() => fetchDataStats()}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors"
            title="Refresh stats"
          >
            <RefreshCw size={16} />
          </button>
        </div>
        <div className="flex flex-row justify-between gap-4 w-full">
          <div className="flex-1 bg-emerald-50 border border-emerald-100 rounded-lg p-3 flex flex-col items-start">
            <span className="text-xs text-emerald-600 font-medium mb-1 flex items-center gap-1">
              Ready to Use
              <InfoWithTooltip tooltip="Files that have been analyzed and are ready for schema generation" />
            </span>
            <span className="text-2xl font-bold text-emerald-700">
              {loadingOOBTaggedFileCount ? (
                <span className="animate-pulse">...</span>
              ) : (
                oobTaggedFileCount
              )}
            </span>
          </div>
          <div className="flex-1 bg-gray-50 border border-gray-100 rounded-lg p-3 flex flex-col items-start">
            <span className="text-xs text-gray-600 font-medium mb-1 flex items-center gap-1">
              Preparation Progress
              <InfoWithTooltip tooltip="How much of your data has been analyzed" />
            </span>
            <span className="text-2xl font-bold text-gray-700">
              {loadingOOBTaggedFileCount ? (
                <span className="animate-pulse">...</span>
              ) : (
                `${Math.round((oobTaggedFileCount / (totalActiveFiles || 1)) * 100)}%`
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Prepare Data Button (if needed) */}
      {!allFilesSynthesized && (
        <button
          onClick={() => setShowPrepareDataDialog(true)}
          className="w-full py-3 px-4 rounded-lg bg-gray-50 border border-gray-300 text-gray-700 hover:bg-gray-100 font-semibold flex items-center justify-center gap-2 transition-all shadow-sm"
        >
          <Zap className="w-4 h-4 text-amber-500" />
          Prepare Data
        </button>
      )}

      {/* Tag Settings (only Tag Format, standardized width) */}
      <div className="bg-white rounded-lg shadow-sm p-0 w-full">
        <div className="p-4">
          <div className="space-y-3">
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                Tag Format
                <InfoWithTooltip tooltip="Choose the type of tag to output. (e.g., Yes/No only outputs Yes or No)" />
              </label>
              <div className="relative tag-format-dropdown">
                <button
                  type="button"
                  onClick={() =>
                    setShowTagFormatDropdown(!showTagFormatDropdown)
                  }
                  className="w-full rounded-xl px-4 py-3 pr-12 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-primary/20 focus:border-primary/30 focus:from-white focus:to-white transition-all hover:border-gray-300 hover:shadow-sm text-left flex items-center gap-2"
                  disabled={structureType !== FLAT_VAR}
                >
                  <span className="text-base">
                    {graphTagType === GRAPH_NODE_TYPES.OPEN_ENDED && "✍️"}
                    {graphTagType === GRAPH_NODE_TYPES.BINARY && "👍"}
                    {graphTagType === GRAPH_NODE_TYPES.DEFINED_VALUES && "🏷️"}
                    {graphTagType === GRAPH_NODE_TYPES.HIERARCHY && "🔀"}
                  </span>
                  <span>
                    {graphTagType === GRAPH_NODE_TYPES.OPEN_ENDED &&
                      "Open Ended Tags"}
                    {graphTagType === GRAPH_NODE_TYPES.BINARY && "Yes/No Tags"}
                    {graphTagType === GRAPH_NODE_TYPES.DEFINED_VALUES &&
                      "Categorical Tags"}
                    {graphTagType === GRAPH_NODE_TYPES.HIERARCHY &&
                      "Hierarchical Tags"}
                  </span>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg px-2 py-1">
                      <svg
                        className={`h-4 w-4 text-gray-500 transition-transform ${showTagFormatDropdown ? "rotate-180" : ""}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </button>

                {/* Custom Dropdown Options */}
                {showTagFormatDropdown && (
                  <div className="absolute z-10 w-full mt-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setGraphTagType(GRAPH_NODE_TYPES.OPEN_ENDED);
                        setShowTagFormatDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        graphTagType === GRAPH_NODE_TYPES.OPEN_ENDED
                          ? "bg-gray-50"
                          : ""
                      }`}
                    >
                      <span className="text-base">✍️</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Open Ended Tags
                        </p>
                        <p className="text-xs text-gray-500">
                          Free-form text responses
                        </p>
                      </div>
                      {graphTagType === GRAPH_NODE_TYPES.OPEN_ENDED && (
                        <svg
                          className="w-4 h-4 text-primary"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setGraphTagType(GRAPH_NODE_TYPES.BINARY);
                        setShowTagFormatDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        graphTagType === GRAPH_NODE_TYPES.BINARY
                          ? "bg-gray-50"
                          : ""
                      }`}
                    >
                      <span className="text-base">👍</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Yes/No Tags
                        </p>
                        <p className="text-xs text-gray-500">
                          Binary true/false values
                        </p>
                      </div>
                      {graphTagType === GRAPH_NODE_TYPES.BINARY && (
                        <svg
                          className="w-4 h-4 text-primary"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setGraphTagType(GRAPH_NODE_TYPES.DEFINED_VALUES);
                        setShowTagFormatDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        graphTagType === GRAPH_NODE_TYPES.DEFINED_VALUES
                          ? "bg-gray-50"
                          : ""
                      }`}
                    >
                      <span className="text-base">🏷️</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Categorical Tags
                        </p>
                        <p className="text-xs text-gray-500">
                          Predefined categories
                        </p>
                      </div>
                      {graphTagType === GRAPH_NODE_TYPES.DEFINED_VALUES && (
                        <svg
                          className="w-4 h-4 text-primary"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setGraphTagType(GRAPH_NODE_TYPES.HIERARCHY);
                        setShowTagFormatDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        graphTagType === GRAPH_NODE_TYPES.HIERARCHY
                          ? "bg-gray-50"
                          : ""
                      }`}
                    >
                      <span className="text-base">🔀</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Hierarchical Tags
                        </p>
                        <p className="text-xs text-gray-500">
                          Multi-level structure
                        </p>
                      </div>
                      {graphTagType === GRAPH_NODE_TYPES.HIERARCHY && (
                        <svg
                          className="w-4 h-4 text-primary"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Deep Suggest Toggle - appears when Hierarchical is selected
            {graphTagType === GRAPH_NODE_TYPES.HIERARCHY && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Deep Suggest
                    </p>
                    <p className="text-xs text-gray-600">
                      Agentic Metadata Suggestion
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setUseDeepSuggestionMode(!useDeepSuggestionMode)
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 ${
                    useDeepSuggestionMode ? "bg-gray-600" : "bg-gray-300"
                  }`}
                >
                  <span className="sr-only">Toggle deep suggest</span>
                  <span
                    className={`${
                      useDeepSuggestionMode ? "translate-x-6" : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out`}
                  />
                </button>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Generate Schema Button - prominent */}
      <div className="flex px-4">
        <button
          onClick={handleAutoSuggestClick}
          className={`w-full mt-2 px-6 py-3 rounded-lg font-bold text-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md text-left
          ${
            !generatingSchema && !disableAutoSuggest && oobTaggedFileCount
              ? "bg-primary text-white hover:bg-primary/80"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }
        `}
          title={!oobTaggedFileCount ? "Please analyze some data first" : ""}
        >
          {generatingSchema ? (
            <>
              <span className="w-4 h-4 border-2 bg-gray-200 border-white border-t-transparent rounded-full animate-spin"></span>
              <span>Thinking ...</span>
            </>
          ) : (
            <>Generate Schema</>
          )}
        </button>
      </div>
      {/* Advanced Options (collapsible, left-aligned, standardized width) */}
      <details
        className="bg-white rounded-lg shadow-sm p-0 mt-2 w-full"
        style={{ textAlign: "left" }}
      >
        <summary className="cursor-pointer px-4 py-2 font-medium text-primary">
          Advanced Options
        </summary>
        <div className="p-4 pt-6 space-y-4">
          {/* Domain Context */}
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
              Domain Context
              <InfoWithTooltip tooltip="Add domain-specific information to generate a more accurate schema" />
            </label>
            <textarea
              className="w-full rounded-lg p-3 bg-gray-50 border-0 text-sm focus:ring-2 focus:ring-primary/50 focus:bg-white"
              value={inputTextContext}
              onChange={(e) => setInputTextContext(e.target.value)}
              placeholder="Add domain-specific information to generate a more accurate schema"
              rows={4}
            />
          </div>

          {/* Advanced controls (schema depth, file filter, context level, suggestion root, max values) */}
          {/* Schema Depth */}
          {/* <div
            className={`flex items-center justify-between space-x-4 ${structureType === FLAT_VAR || graphTagType === GRAPH_NODE_TYPES.HIERARCHY ? "opacity-50" : ""}`}
          >
            <div className="flex items-center space-x-2 w-1/3">
              <label className="text-sm font-medium text-gray-700 text-left flex-1">
                Schema Depth
                <InfoWithTooltip
                  tooltip={
                    structureType === FLAT_VAR
                      ? "Depth is fixed at 1 level for flat structures"
                      : graphTagType === GRAPH_NODE_TYPES.HIERARCHY
                        ? "Depth is fixed at 2 levels for hierarchical tags"
                        : "How many levels deep your schema should be. 2-5 levels usually works best."
                  }
                />
              </label>
            </div>
            <div className="w-2/3 flex flex-col space-y-1">
              <input
                type="range"
                min={1}
                max={10}
                step={1}
                value={maxDepth}
                onChange={(e) => setMaxDepth(parseInt(e.target.value))}
                disabled={structureType === FLAT_VAR || graphTagType === GRAPH_NODE_TYPES.HIERARCHY}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>
                  {maxDepth} level{maxDepth !== 1 ? "s" : ""}
                </span>
                <span className="text-gray-400">
                  {structureType === FLAT_VAR
                    ? "Fixed for flat structure"
                    : graphTagType === GRAPH_NODE_TYPES.HIERARCHY
                      ? "Fixed for hierarchical"
                      : maxDepth < 2
                        ? "Too shallow"
                        : maxDepth > 5
                          ? "Very deep"
                          : "Recommended"}
                </span>
              </div>
            </div>
          </div> */}

          {/* File Filter */}
          {/* <div className="flex items-center justify-between space-x-4 my-6">
            <div className="flex items-center space-x-2 w-1/3">
              <label className="text-sm font-medium text-gray-700 text-left flex-1">
                File Filter
                <InfoWithTooltip tooltip="Filter which files should be used for schema generation" />
              </label>
            </div>
            <div className="w-2/3">
              <button
                onClick={() => setShowMetadataTable(true)}
                className={`w-full flex items-center justify-between px-4 py-2 border rounded-md hover:bg-gray-50 ${
                  tableConditionsNew &&
                  tableConditionsNew.children &&
                  tableConditionsNew.children.length > 0
                    ? "border-primary bg-primary/5"
                    : "border-gray-300"
                }`}
              >
                <div className="flex items-center">
                  <Filter
                    size={12}
                    className={`ml-1 mr-3 ${tableConditionsNew && tableConditionsNew.children && tableConditionsNew.children.length > 0 ? "text-primary" : "text-gray-500"}`}
                  />
                  <span
                    className={`text-sm ${tableConditionsNew && tableConditionsNew.children && tableConditionsNew.children.length > 0 ? "text-primary font-medium" : "text-gray-600"}`}
                  >
                    {tableConditionsNew &&
                    tableConditionsNew.children &&
                    tableConditionsNew.children.length > 0
                      ? "Applied"
                      : "Select files..."}
                  </span>
                </div>
                {tableConditionsNew &&
                  tableConditionsNew.children &&
                  tableConditionsNew.children.length > 0 && (
                    <div className="flex items-center">
                      <span className="text-xs text-primary mr-2 bg-primary/10 px-2 py-0.5 rounded-full">
                        Active
                      </span>
                      <ChevronRight size={16} className="text-primary" />
                    </div>
                  )}
                {(!tableConditionsNew ||
                  !tableConditionsNew.children ||
                  tableConditionsNew.children.length === 0) && (
                  <ChevronRight size={16} className="text-gray-400" />
                )}
              </button>
            </div>
          </div> */}

          {/* Max Values */}
          <div className="flex items-center justify-between space-x-4">
            <div className="flex items-center space-x-2 w-1/3">
              <label className="text-sm font-medium text-gray-700 text-left flex-1">
                Max Values
                <InfoWithTooltip tooltip="Choose how maximum values are determined for tags. Default allows the system to optimize values automatically." />
              </label>
            </div>
            <div className="w-2/3 max-values-dropdown relative">
              <button
                type="button"
                onClick={() => setShowMaxValuesDropdown(!showMaxValuesDropdown)}
                className="w-full rounded-xl px-4 py-3 pr-12 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-primary/20 focus:border-primary/30 focus:from-white focus:to-white transition-all hover:border-gray-300 hover:shadow-sm text-left flex items-center gap-2"
              >
                <span className="text-base">
                  {useDefaultMaxValues ? "🎯" : "♾️"}
                </span>
                <span>
                  {useDefaultMaxValues ? "Auto-generate" : "No Limit"}
                </span>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg px-2 py-1">
                    <svg
                      className={`h-4 w-4 text-gray-500 transition-transform ${showMaxValuesDropdown ? "rotate-180" : ""}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </button>

              {/* Custom Dropdown Options */}
              {showMaxValuesDropdown && (
                <div className="absolute z-10 w-full mt-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setUseDefaultMaxValues(true);
                      setShowMaxValuesDropdown(false);
                    }}
                    className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                      useDefaultMaxValues ? "bg-gray-50" : ""
                    }`}
                  >
                    <span className="text-base">🎯</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Auto-generate
                      </p>
                      <p className="text-xs text-gray-500">
                        System optimizes values automatically
                      </p>
                    </div>
                    {useDefaultMaxValues && (
                      <svg
                        className="w-4 h-4 text-primary"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setUseDefaultMaxValues(false);
                      setShowMaxValuesDropdown(false);
                    }}
                    className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                      !useDefaultMaxValues ? "bg-gray-50" : ""
                    }`}
                  >
                    <span className="text-base">♾️</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        No Limit
                      </p>
                      <p className="text-xs text-gray-500">
                        Allow unlimited values per tag
                      </p>
                    </div>
                    {!useDefaultMaxValues && (
                      <svg
                        className="w-4 h-4 text-primary"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </details>

      {/* Mini Model Warning Dialog */}
      <Modal
        open={showMiniWarning}
        onClose={() => setShowMiniWarning(false)}
        aria-labelledby="mini-warning-title"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl p-6 w-[90vw] max-w-[500px] mx-4">
          <h2
            className="text-xl font-semibold mb-4 text-red"
            id="mini-warning-title"
          >
            Model Not Recommended
          </h2>
          <p className="text-gray-600 mb-6">
            GPT-4o-mini is not recommended for tag suggestion as it may produce
            less accurate and comprehensive results. Please switch to another
            endpoint in your LLM configuration for better results.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowMiniWarning(false)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowMiniWarning(false);
                setShowConfirmDialog(true);
              }}
              className="px-4 py-2 rounded-lg bg-amber-700 text-white font-semibold hover:bg-amber-700"
            >
              Proceed Anyway
            </button>
          </div>
        </div>
      </Modal>

      {/* Prepare Data Confirmation Dialog */}
      <Modal
        open={showPrepareDataDialog}
        onClose={() => !isPrepareLoading && setShowPrepareDataDialog(false)}
        aria-labelledby="confirm-prepare-data-title"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl p-6 w-[90vw] max-w-[500px] mx-4">
          <h2
            className="text-xl font-semibold mb-4"
            id="confirm-prepare-data-title"
          >
            {isPrepareLoading ? "Preparing Data..." : "Prepare Data"}
          </h2>
          <p className="text-gray-600 mb-6">
            {isPrepareLoading
              ? "Processing your data. This may take a few moments..."
              : "Analyzing your data will enable Deasy to create better auto-suggestions. How would you like to proceed?"}
          </p>
          {isPrepareLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="flex flex-col items-center gap-4">
                <svg
                  className="animate-spin h-10 w-10 text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <p className="text-gray-500">Processing your request...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4 mb-6">
                <button
                  onClick={handlePrepareAll}
                  disabled={isPrepareLoading}
                  className="w-full px-4 py-3 rounded-lg bg-primary text-white font-semibold hover:bg-emerald-700 flex items-center justify-center gap-2"
                >
                  {isPrepareLoading ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white mr-2"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Database size={18} />
                      Prepare All Data
                      <span className="text-sm opacity-80 ml-1">
                        (
                        {getTotalDatasetsCount(
                          hierarchyStats,
                          selectedNodeData?.nodePath,
                        )}{" "}
                        files)
                      </span>
                    </>
                  )}
                </button>

                <button
                  onClick={handlePrepareSelected}
                  disabled={isPrepareLoading}
                  className="w-full px-4 py-3 rounded-lg border border-primary text-primary font-semibold hover:bg-primary/10 flex items-center justify-center gap-2"
                >
                  <Layers size={18} />
                  Select Files to Prepare
                </button>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowPrepareDataDialog(false)}
                  disabled={isPrepareLoading}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* File Picker Modal */}
      <FilePickerModal
        openFilePicker={openFilePicker}
        setOpenFilePicker={setOpenFilePicker}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        selectAllForEntireDB={false}
      />

      {/* Confirmation Dialog */}
      <Modal
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        aria-labelledby="confirm-auto-suggest-title"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl p-6 w-[90vw] max-w-[500px] mx-4">
          <h2
            className="text-xl font-semibold mb-4"
            id="confirm-auto-suggest-title"
          >
            Confirm Auto-Suggestion
          </h2>
          <p className="text-gray-600 mb-6">
            This will generate a hierarchical schema tree based on your dataset
            context. The process may take a few seconds depending on the size of
            your dataset. Do you want to proceed?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowConfirmDialog(false)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setShowConfirmDialog(false);
                await handleAutoSuggest();
              }}
              className="px-4 py-2 rounded-lg bg-primary text-white font-semibold hover:bg-emerald-700"
            >
              Proceed
            </button>
          </div>
        </div>
      </Modal>

      {showMetadataTable && (
        <Modal
          open={showMetadataTable}
          onClose={() => setShowMetadataTable(false)}
          aria-labelledby="metadata-table-title"
          sx={{
            "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.1)" },
            "& .MuiModal-root": { zIndex: 1300 },
          }}
        >
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-lg w-[95vw] max-w-[1600px] h-[85vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="px-6 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter size={18} className="text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Filter Files
                  </h2>
                </div>
                <button
                  onClick={() => setShowMetadataTable(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-full"
                >
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="overflow-auto h-full p-4 bg-gray-50">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-visible">
                    <MetadataTable
                      nodeData={selectedNodeData}
                      source="graphFilter"
                      compact={true}
                      backendFilters={localFilters}
                      onFiltersChange={setLocalFilters}
                      conditionsNew={tableConditionsNew}
                      setConditionsNew={setTableConditionsNew}
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-gray-200 bg-white flex justify-end gap-2">
                <button
                  onClick={() => setShowMetadataTable(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowMetadataTable(false)}
                  className="px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-md"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Tag Selection Modal */}
      <TagSelectionModal
        open={showTagSelectionModal}
        onClose={() => {
          setShowTagSelectionModal(false);
          setGeneratingSchema(false);
        }}
        suggestedTags={suggestedTagsData}
        tagNotFoundRates={tagNotFoundRates}
        onConfirm={handleTagsConfirmed}
        isHierarchical={graphTagType === GRAPH_NODE_TYPES.HIERARCHY}
      />
    </div>
  );
};
